import { EnvConfig } from '@gripp/shared-logic';

/**
 * Get the base AppsFlyer OneLink URL for the current environment
 */
export const getOneLinkBaseUrl = (): string => {
  return EnvConfig.prefix === 'prod'
    ? 'https://gripp.onelink.me/JIWC/fodtl90i'
    : 'https://gripp-beta.onelink.me/sv4m/q37k80m3';
};

/**
 * Whether this browser is a phone or tablet, i.e. whether the OneLink is the
 * right destination for a link the app can handle itself once installed.
 *
 * The same test `invitation.tsx` and `anonymousAsset.tsx` each spell out
 * inline; lives here because the decision only ever ends in this module's
 * redirect. Those two are left as they are — a shared surface each, and
 * neither is in this change.
 */
export const isMobileBrowser = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

/**
 * Build an AppsFlyer OneLink URL with deep link parameters
 * @param params - Key-value pairs to include in deep_link_value
 */
export const buildOneLinkUrl = (params: Record<string, string>): string => {
  const base = getOneLinkBaseUrl();
  const deepLinkValue = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${base}?af_dp=gripp://&deep_link_value=${encodeURIComponent(
    deepLinkValue
  )}`;
};

// The ids ride in `deep_link_value`, which is URL-encoded into the redirect URL.
// Bound the combined payload so the URL can't blow past safe length limits
// (~2KB) when a visitor both reported issues and completed routines anonymously
// (each id is a ~36-char UUID). One list present → full budget; both present →
// each capped to half, so the combined total stays at the single-list budget.
const MAX_ANONYMOUS_IDS = 30;

/**
 * Builds the anonymous-activity deep-link params (issue + routine ids) with a
 * shared cap on the combined id count. Centralized so every OneLink call site
 * (drop page, download badges, invitation redirect) stays within the same
 * length budget instead of each capping its own list independently.
 */
export const buildAnonymousActivityParams = (
  anonymousIssueIds?: string[],
  anonymousRoutineIds?: string[]
): Record<string, string> => {
  const hasIssues = !!anonymousIssueIds?.length;
  const hasRoutines = !!anonymousRoutineIds?.length;
  const cap = hasIssues && hasRoutines ? MAX_ANONYMOUS_IDS / 2 : MAX_ANONYMOUS_IDS;

  const params: Record<string, string> = {};
  if (hasIssues) {
    params.anonymousIssueIds = anonymousIssueIds.slice(0, cap).join(',');
  }
  if (hasRoutines) {
    params.anonymousRoutineIds = anonymousRoutineIds.slice(0, cap).join(',');
  }
  return params;
};
